/**
 * The settings-editor state machine (M31 task-16) behind both the hub
 * Configuration section and an extension's settings page (HUB-103): the
 * entry list, per-key field errors, the busy key, the 503
 * `encryption_key_unconfigured` banner, and save/reset over a small API the
 * caller supplies. Extracted so the two pages cannot drift.
 */
import { useEffect, useRef, useState } from 'react';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { buildPutBody, putErrorMessage, type ValueKind } from '@/lib/settings-config';
import type { SettingRowEntry } from './SettingRow';

/** Method syntax on purpose (AGENTS.md) — implementations narrow the entry type. */
export interface SettingsEditorApi<E extends SettingRowEntry> {
  list(): Promise<E[]>;
  put(key: string, body: { value: unknown }): Promise<E>;
  remove(key: string): Promise<void>;
}

export function useSettingsEditor<E extends SettingRowEntry>(api: SettingsEditorApi<E>) {
  const apiRef = useRef(api);
  apiRef.current = api;
  const [entries, setEntries] = useState<E[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [encKeyError, setEncKeyError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setEntries(await apiRef.current.list());
      setLoadError(null);
    } catch (err) {
      setLoadError((err as Error).message);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const setFieldError = (key: string, message: string | null) =>
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message === null) delete next[key];
      else next[key] = message;
      return next;
    });

  const onSaveFailure = (key: string, err: unknown) => {
    if (err instanceof HubClientError && err.code === 'encryption_key_unconfigured') {
      // M27 admin-config banner state — same Alert the models/identity
      // screens surface when HUB_ENC_KEY is missing (503 until it is set).
      setEncKeyError(putErrorMessage(err.body, err.message));
      return;
    }
    if (
      err instanceof HubClientError &&
      (err.code === 'validation_error' || err.code === 'unknown_setting_key')
    ) {
      setFieldError(key, putErrorMessage(err.body, err.message));
      return;
    }
    setSaveError((err as Error).message);
  };

  const save = async (entry: E, kind: ValueKind, raw: boolean | string): Promise<boolean> => {
    const parsed = buildPutBody(kind, raw);
    if (!parsed.ok) {
      setFieldError(entry.key, parsed.message);
      return false;
    }
    setBusyKey(entry.key);
    setSaveError(null);
    setFieldError(entry.key, null);
    try {
      const row = await apiRef.current.put(entry.key, parsed.body);
      setEntries((prev) => (prev ? prev.map((e) => (e.key === entry.key ? row : e)) : prev));
      return true;
    } catch (err) {
      onSaveFailure(entry.key, err);
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (key: string) => {
    setBusyKey(key);
    setSaveError(null);
    setFieldError(key, null);
    try {
      await apiRef.current.remove(key);
      // 204 carries no body — re-list so the row shows the fallback value the
      // delete uncovered.
      await refresh();
    } catch (err) {
      onSaveFailure(key, err);
    } finally {
      setBusyKey(null);
    }
  };

  return { entries, loadError, saveError, encKeyError, fieldErrors, busyKey, save, reset, refresh };
}
